import {
  BarcodeEntry,
  BarcodeQuestionConfig,
  BarcodeResponseState,
  useGrippContext,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { TextInput as TextInputCropCircle } from '../../../../crop-circle/Components/TextInput/TextInput';
import { MobileIcon, XIcon } from '../../../../crop-circle/icons';
import { tokens } from '../../../../crop-circle/tokens';
import { MaskedNumberInput, useFormContext } from '../../../../forms';
import {
  RoutineQuestionExecutionForm,
  RoutineQuestionExecutionFormProps,
} from '../types';

export const BarcodeQuestionExecutionForm: RoutineQuestionExecutionForm<
  BarcodeQuestionConfig,
  BarcodeResponseState
> = (
  props: RoutineQuestionExecutionFormProps<
    BarcodeQuestionConfig,
    BarcodeResponseState
  >
) => {
  const { t } = useTranslation();
  const fieldName = `responses.${props.responseIndex}.barcodes`;
  const { control, setValue } = useFormContext();
  const { barcodeScannerComponent: BarcodeScanner } = useGrippContext();
  const [scanTargetIndex, setScanTargetIndex] = useState<number | null>(null);
  const scanTargetRef = useRef<number | null>(null);

  const defaultBarcodes: BarcodeEntry[] = props?.isIssueEditRoutine
    ? (props?.currentState?.state as any)?.responses?.[props.responseIndex]
        ?.barcodes ?? [{ value: '' }]
    : [{ value: '' }];

  control.register(fieldName, { value: defaultBarcodes });

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
  });

  const handleRemoveEntry = useCallback(
    (index: number) => {
      if (fields.length > 1) {
        remove(index);
      } else {
        setValue(`${fieldName}.0.value`, '');
        setValue(`${fieldName}.0.quantity`, 1);
      }
    },
    [fields.length, fieldName, remove, setValue]
  );

  const handleScanPress = (index: number) => {
    scanTargetRef.current = index;
    setScanTargetIndex(index);
  };

  const handleScanned = useCallback(
    (value: string) => {
      const targetIndex = scanTargetRef.current;
      if (targetIndex !== null) {
        setValue(`${fieldName}.${targetIndex}.value`, value);
        if (props.config.allowMultiple && targetIndex === fields.length - 1) {
          append({
            value: '',
            quantity: props.config.includeQuantity ? 1 : undefined,
          });
        }
      }
      scanTargetRef.current = null;
      setScanTargetIndex(null);
    },
    [
      fieldName,
      fields.length,
      props.config.allowMultiple,
      props.config.includeQuantity,
      append,
      setValue,
    ]
  );

  const handleScanDismiss = () => {
    scanTargetRef.current = null;
    setScanTargetIndex(null);
  };

  // Auto-append/trim trailing empty rows when allowMultiple is enabled
  const watchedBarcodes: BarcodeEntry[] =
    useWatch({ control, name: fieldName }) ?? [];
  const isAppendingRef = useRef(false);
  useEffect(() => {
    if (!props.config.allowMultiple || watchedBarcodes.length === 0) return;
    if (isAppendingRef.current) {
      isAppendingRef.current = false;
      return;
    }
    const lastValue = watchedBarcodes[watchedBarcodes.length - 1]?.value;
    if (lastValue) {
      isAppendingRef.current = true;
      append({
        value: '',
        quantity: props.config.includeQuantity ? 1 : undefined,
      });
    } else if (watchedBarcodes.length > 1) {
      // Remove extra trailing empty rows (keep exactly one empty row at the end)
      let lastNonEmptyIndex = -1;
      for (let i = watchedBarcodes.length - 1; i >= 0; i--) {
        if (watchedBarcodes[i].value) {
          lastNonEmptyIndex = i;
          break;
        }
      }
      const emptyTailCount = watchedBarcodes.length - 1 - lastNonEmptyIndex;
      if (emptyTailCount > 1) {
        // Remove from the end, keeping one empty row
        for (
          let i = watchedBarcodes.length - 1;
          i > lastNonEmptyIndex + 1;
          i--
        ) {
          remove(i);
        }
      }
    }
  }, [
    watchedBarcodes,
    props.config.allowMultiple,
    props.config.includeQuantity,
    append,
    remove,
  ]);

  const placeholder = t(
    BarcodeScanner
      ? 'routines.workflow.execution.barcodeScanner.placeholder'
      : 'routines.workflow.execution.barcodeScanner.placeholderManual'
  );

  return (
    <View style={styles.container}>
      {BarcodeScanner && scanTargetIndex !== null && (
        <BarcodeScanner
          onScanned={handleScanned}
          onDismiss={handleScanDismiss}
        />
      )}
      {fields.map((field, index) => (
        <BarcodeEntryRow
          key={field.id}
          index={index}
          fieldName={fieldName}
          control={control}
          placeholder={placeholder}
          config={props.config}
          onRemove={handleRemoveEntry}
          onScanPress={BarcodeScanner ? handleScanPress : undefined}
          t={t}
        />
      ))}
    </View>
  );
};

type BarcodeEntryRowProps = {
  index: number;
  fieldName: string;
  control: ReturnType<typeof useFormContext>['control'];
  placeholder: string;
  config: BarcodeQuestionConfig;
  onRemove: (index: number) => void;
  onScanPress?: (index: number) => void;
  t: ReturnType<typeof useTranslation>['t'];
};

const BarcodeEntryRow = ({
  index,
  fieldName,
  control,
  placeholder,
  config,
  onRemove,
  onScanPress,
  t,
}: BarcodeEntryRowProps) => {
  const barcodeValue: string =
    useWatch({ control, name: `${fieldName}.${index}.value` }) ?? '';
  const isFilled = barcodeValue.length > 0;

  const getRightIcon = () => {
    if (isFilled) {
      return (
        <Pressable
          onPress={() => onRemove(index)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t(
            'routines.workflow.execution.barcodeScanner.remove'
          )}
        >
          <XIcon size={16} color={tokens.colors.neutral[800]} />
        </Pressable>
      );
    }

    if (onScanPress) {
      return (
        <Pressable
          onPress={() => onScanPress(index)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t(
            'routines.workflow.execution.barcodeScanner.placeholder'
          )}
        >
          <MobileIcon size={16} color={tokens.colors.neutral[500]} />
        </Pressable>
      );
    }

    return <MobileIcon size={16} color={tokens.colors.neutral[500]} />;
  };

  return (
    <View style={styles.entryContainer}>
      <Controller
        control={control}
        name={`${fieldName}.${index}.value`}
        render={({ field: { onChange, value } }) => {
          return (
            <TextInputCropCircle
              placeholder={placeholder}
              value={value ?? ''}
              onChangeText={onChange}
              hideBorder
              clearable={false}
              rightIcon={getRightIcon()}
              height={52}
            />
          );
        }}
      />
      {config.includeQuantity && isFilled ? (
        <View style={styles.stepperContainer}>
          <Controller
            control={control}
            name={`${fieldName}.${index}.quantity`}
            defaultValue={1}
            render={({ field: { onChange, onBlur, value: qty } }) => (
              <MaskedNumberInput
                value={qty !== undefined ? String(qty) : '1'}
                onChangeText={(val) => {
                  const str = `${val}`.trim();
                  onChange(str === '' ? undefined : Number(str));
                }}
                onBlur={onBlur}
                decimalPlaces={0}
                showStepper
                height={48}
                stepperButtonStyle={styles.stepperButton}
                stepperIconColor={tokens.colors.neutral[800]}
              />
            )}
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  entryContainer: {
    gap: 4,
  },
  stepperContainer: {
    width: '100%',
    height: 48,
    maxHeight: 48,
    flexDirection: 'row',
    borderRadius: 4,
    overflow: 'hidden',
  },
  stepperButton: {
    width: 48,
    backgroundColor: tokens.colors.neutral[200],
    borderRadius: 0,
    borderWidth: 0,
  },
});
